import { useState, useEffect, useMemo } from 'react';
import { API } from '@/App';
import {
  MagnifyingGlass, Funnel, Users, GraduationCap, Handshake,
  CreditCard, ChatCircle, Gear, Headset, ChartBar, Cloud,
  Factory, Shield, Storefront, Bank, X, CaretRight, Lightning
} from '@phosphor-icons/react';

const CATEGORY_ICONS = {
  hr: Users, education: GraduationCap, crm: Handshake, finance: CreditCard,
  comms: ChatCircle, devops: Gear, support: Headset, marketing: ChartBar,
  data: Cloud, erp: Factory, identity: Shield, ecommerce: Storefront, banking: Bank,
};

const TIER_CONFIG = {
  T1: { label: 'Tier 1', color: 'kova-cyan', desc: 'Build First' },
  T2: { label: 'Tier 2', color: 'kova-amber', desc: 'High Value' },
  T3: { label: 'Tier 3', color: 'kova-slate', desc: 'Vertical' },
};

const DEMAND_CONFIG = {
  high: { label: 'High', color: 'kova-green' },
  medium: { label: 'Med', color: 'kova-amber' },
  low: { label: 'Low', color: 'kova-slate' },
};

export default function APICatalog({ onSelectTemplate, onClose }) {
  const [catalog, setCatalog] = useState({ templates: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTier, setActiveTier] = useState(null);

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await fetch(`${API}/catalog/catalog`, { credentials: 'include' });
        if (res.ok) setCatalog(await res.json());
      } catch (e) { console.error('Catalog fetch error:', e); }
      setLoading(false);
    };
    fetchCatalog();
  }, []);

  const filtered = useMemo(() => {
    let items = catalog.templates || [];
    if (activeCategory) items = items.filter(t => t.category === activeCategory);
    if (activeTier) items = items.filter(t => t.tier === activeTier);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(t => t.name.toLowerCase().includes(q) || t.use_case.toLowerCase().includes(q));
    }
    return items;
  }, [catalog.templates, activeCategory, activeTier, search]);

  const grouped = useMemo(() => {
    const groups = {};
    for (const t of filtered) {
      if (!groups[t.category]) groups[t.category] = [];
      groups[t.category].push(t);
    }
    return groups;
  }, [filtered]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    for (const t of (catalog.templates || [])) {
      counts[t.category] = (counts[t.category] || 0) + 1;
    }
    return counts;
  }, [catalog.templates]);

  const handleSelect = (template) => {
    onSelectTemplate({
      name: template.name,
      description: template.use_case,
      base_url: template.base_url,
      method: 'GET',
      auth_type: template.auth_method?.toLowerCase().includes('oauth') ? 'bearer'
        : template.auth_method?.toLowerCase().includes('api key') ? 'api_key' : 'none',
      auth_config: {},
      headers: {},
      _catalog_tier: template.tier,
      _catalog_category: template.category,
    });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <span className="font-mono text-kova-cyan text-xs">
          <span className="ascii-spinner mr-2"></span>LOADING CATALOG
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[80vh]" data-testid="api-catalog">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-kova-cyan/12 flex-shrink-0">
        <div>
          <h2 className="font-heading text-lg font-bold text-kova-white">API Catalog</h2>
          <p className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate mt-0.5">
            {catalog.templates?.length || 0} integrations across {catalog.categories?.length || 0} categories
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Tier Filter Pills */}
          {Object.entries(TIER_CONFIG).map(([tier, cfg]) => (
            <button
              key={tier}
              data-testid={`filter-${tier}`}
              onClick={() => setActiveTier(activeTier === tier ? null : tier)}
              className={`font-mono text-[8px] tracking-[1.5px] uppercase px-2 py-1 border transition-colors ${
                activeTier === tier
                  ? `border-${cfg.color} bg-${cfg.color}/15 text-${cfg.color}`
                  : 'border-kova-cyan/12 text-kova-slate hover:text-kova-silv'
              }`}
            >
              {tier}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative py-3 flex-shrink-0">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kova-slate" />
        <input
          data-testid="catalog-search"
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search APIs by name or use case..."
          className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-[11px] pl-8 pr-3 py-2 focus:outline-none focus:border-kova-cyan placeholder:text-kova-slate"
        />
      </div>

      <div className="flex flex-1 gap-0 overflow-hidden min-h-0">
        {/* Category Sidebar */}
        <div className="w-44 flex-shrink-0 border-r border-kova-cyan/12 overflow-y-auto pr-1">
          <button
            onClick={() => setActiveCategory(null)}
            data-testid="cat-all"
            className={`w-full text-left px-2 py-1.5 text-[10px] font-mono uppercase tracking-[1px] transition-colors ${
              !activeCategory ? 'text-kova-cyan bg-kova-cyan/10' : 'text-kova-slate hover:text-kova-silv'
            }`}
          >
            All ({catalog.templates?.length || 0})
          </button>
          {(catalog.categories || []).map(cat => {
            const Icon = CATEGORY_ICONS[cat.id] || Gear;
            const count = categoryCounts[cat.id] || 0;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                data-testid={`cat-${cat.id}`}
                className={`w-full text-left flex items-center gap-2 px-2 py-1.5 text-[10px] font-mono tracking-[0.5px] transition-colors ${
                  activeCategory === cat.id ? 'text-kova-cyan bg-kova-cyan/10' : 'text-kova-slate hover:text-kova-silv'
                }`}
              >
                <Icon size={12} weight={activeCategory === cat.id ? 'fill' : 'regular'} />
                <span className="truncate flex-1">{cat.label}</span>
                <span className="text-[8px] opacity-60">{count}</span>
              </button>
            );
          })}
        </div>

        {/* API Grid */}
        <div className="flex-1 overflow-y-auto pl-3">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-kova-slate font-mono text-xs">
              No APIs match your filters
            </div>
          ) : activeCategory ? (
            /* Flat list when category is selected */
            <div className="space-y-1">
              {filtered.map(tpl => (
                <APICard key={tpl.template_id} template={tpl} onSelect={handleSelect} />
              ))}
            </div>
          ) : (
            /* Grouped by category when showing all */
            Object.entries(grouped).map(([catId, items]) => {
              const catInfo = (catalog.categories || []).find(c => c.id === catId);
              const Icon = CATEGORY_ICONS[catId] || Gear;
              return (
                <div key={catId} className="mb-4">
                  <div className="flex items-center gap-2 mb-1.5 sticky top-0 bg-kova-bg py-1 z-10">
                    <Icon size={12} className="text-kova-cyan" />
                    <span className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate">
                      {catInfo?.label || catId} ({items.length})
                    </span>
                  </div>
                  <div className="space-y-1">
                    {items.map(tpl => (
                      <APICard key={tpl.template_id} template={tpl} onSelect={handleSelect} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function APICard({ template, onSelect }) {
  const tierCfg = TIER_CONFIG[template.tier] || TIER_CONFIG.T3;
  const demandCfg = DEMAND_CONFIG[template.demand] || DEMAND_CONFIG.low;

  return (
    <button
      onClick={() => onSelect(template)}
      data-testid={`catalog-${template.template_id}`}
      className="w-full text-left flex items-center gap-3 px-3 py-2.5 border border-kova-cyan/6 hover:border-kova-cyan/25 bg-kova-card hover:bg-kova-card2 transition-all group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-heading text-sm font-bold text-kova-white group-hover:text-kova-cyan transition-colors truncate">
            {template.name}
          </span>
          <span className={`font-mono text-[7px] tracking-[1px] uppercase px-1 py-px border border-${tierCfg.color}/30 text-${tierCfg.color} flex-shrink-0`}>
            {template.tier}
          </span>
        </div>
        <p className="text-kova-silv text-[11px] truncate mt-0.5">{template.use_case}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="font-mono text-[8px] tracking-[1px] text-kova-slate uppercase">{template.auth_method}</span>
        <span className={`w-1.5 h-1.5 rounded-full bg-${demandCfg.color}`} title={`${demandCfg.label} demand`} />
        <CaretRight size={12} className="text-kova-slate group-hover:text-kova-cyan transition-colors" />
      </div>
    </button>
  );
}
